import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { MIN_LEN } from '@/constants';
import { getCustomerInitials } from '@/utils/user.utils';
import type {
  ExternalInvitationContactRowModel,
  ExternalInvitationManualRowModel,
  ExternalInvitationManualValues,
  ExternalInvitationSelectedParticipantModel,
  ExternalInvitationTabKey,
} from '@/types/ExternalInvitationStep.types';
import {
  EMPTY_MANUAL_VALUES,
  createRowIdentifier,
  createInitialManualRows,
  resolveHasPendingManualEntries,
  resolveEmailErrorMessage,
  resolveIsAddDisabled,
} from '@/utils/externalInvitations.utils';

interface ExternalUser {
  id: string;
  name: string;
  email: string;
  deptOrg: string;
  title?: string;
}

interface UseExternalInvitationsFormParams {
  externalUsers: ExternalUser[];
  existingUserEmails: string[];
  disabledContactIds: string[];
  newExternalUsers: Array<{
    name: string;
    email: string;
    phoneNumber: string;
    department?: string;
  }>;
  onAddContact: (contactId: string) => void;
  onRemoveContact: (contactId: string) => void;
  onAddNewExternalUser: (user: {
    name: string;
    email: string;
    phoneNumber: string;
    department?: string;
  }) => void;
  onRemoveNewExternalUser: (userIndex: number) => void;
  onPendingManualEntriesChange?: (hasPendingEntries: boolean) => void;
}

export interface UseExternalInvitationsFormState {
  activeTabValue: ExternalInvitationTabKey;
  contactsSearchValue: string;
  contactRows: ExternalInvitationContactRowModel[];
  selectedParticipants: ExternalInvitationSelectedParticipantModel[];
  manualRows: ExternalInvitationManualRowModel[];
}

export interface UseExternalInvitationsFormHandlers {
  handleTabChange: (nextTab: ExternalInvitationTabKey) => void;
  handleContactsSearchValueChange: (nextValue: string) => void;
  handleAddContact: (contactId: string) => void;
  handleRemoveSelectedParticipant: (participantId: string) => void;
  handleManualRowValuesChange: (rowId: string, nextValues: ExternalInvitationManualValues) => void;
  handleAddManualRow: () => void;
  handleRemoveManualRow: (rowId: string) => void;
  handleAddRowToSix: (rowId: string) => void;
}

export interface UseExternalInvitationsFormReturn {
  state: UseExternalInvitationsFormState;
  handlers: UseExternalInvitationsFormHandlers;
}

export function useExternalInvitationsForm(
  params: UseExternalInvitationsFormParams,
): UseExternalInvitationsFormReturn {
  const {
    externalUsers,
    existingUserEmails,
    disabledContactIds,
    newExternalUsers,
    onAddContact,
    onRemoveContact,
    onAddNewExternalUser,
    onRemoveNewExternalUser,
    onPendingManualEntriesChange,
  } = params;

  const [activeTabValue, setActiveTabValue] = useState<ExternalInvitationTabKey>('contacts');
  const [contactsSearchValue, setContactsSearchValue] = useState<string>('');
  const [manualRowValues, setManualRowValues] = useState<
    Array<{ id: string; values: ExternalInvitationManualValues }>
  >(createInitialManualRows);

  const normalizedQueryValue = contactsSearchValue.trim().toLowerCase();

  const normalizedExistingUserEmails = useMemo(
    () => new Set(existingUserEmails.map((email) => email.trim().toLowerCase())),
    [existingUserEmails],
  );

  const contactRows = useMemo<ExternalInvitationContactRowModel[]>(
    () =>
      externalUsers
        .filter((externalUser) => !disabledContactIds.includes(externalUser.id))
        .filter((externalUser) => {
          if (normalizedQueryValue.length === MIN_LEN) return true;

          const nameMatches = externalUser.name.toLowerCase().includes(normalizedQueryValue);
          const emailMatches = externalUser.email.toLowerCase().includes(normalizedQueryValue);
          const organizationMatches = externalUser.deptOrg
            .toLowerCase()
            .includes(normalizedQueryValue);

          return nameMatches || emailMatches || organizationMatches;
        })
        .map((externalUser) => ({
          contactId: externalUser.id,
          name: externalUser.name,
          email: externalUser.email,
          organization: externalUser.deptOrg,
          title: externalUser.title,
          initials: getCustomerInitials(externalUser.name),
        })),
    [disabledContactIds, externalUsers, normalizedQueryValue],
  );

  const selectedParticipants = useMemo<ExternalInvitationSelectedParticipantModel[]>(
    () => [
      ...externalUsers
        .filter((externalUser) => disabledContactIds.includes(externalUser.id))
        .map((externalUser) => ({
          id: `contact:${externalUser.id}`,
          name: externalUser.name,
          email: externalUser.email,
        })),
      ...newExternalUsers.map((externalUser, index) => ({
        id: `manual:${String(index)}`,
        name: externalUser.name,
        email: externalUser.email,
      })),
    ],
    [disabledContactIds, externalUsers, newExternalUsers],
  );

  const manualRows = useMemo<ExternalInvitationManualRowModel[]>(
    () =>
      manualRowValues.map((manualRow) => {
        const normalizedManualEmail = manualRow.values.email.trim().toLowerCase();
        const isDuplicateEmail = normalizedManualEmail.length > MIN_LEN
          && normalizedExistingUserEmails.has(normalizedManualEmail);

        return {
          id: manualRow.id,
          values: manualRow.values,
          emailErrorMessage: resolveEmailErrorMessage(manualRow.values, { isDuplicateEmail }),
          isAddDisabled: resolveIsAddDisabled(manualRow.values, { isDuplicateEmail }),
        };
      }),
    [manualRowValues, normalizedExistingUserEmails],
  );

  const hasPendingManualEntries = useMemo(
    () => resolveHasPendingManualEntries(manualRowValues.map((row) => row.values)),
    [manualRowValues],
  );

  useLayoutEffect(() => {
    onPendingManualEntriesChange?.(hasPendingManualEntries);
  }, [hasPendingManualEntries, onPendingManualEntriesChange]);

  useEffect(() => () => {
      onPendingManualEntriesChange?.(false);
    }, [onPendingManualEntriesChange]);

  const handleTabChange = useCallback((nextTab: ExternalInvitationTabKey): void => {
    setActiveTabValue(nextTab);
  }, []);

  const handleContactsSearchValueChange = useCallback((nextValue: string): void => {
    setContactsSearchValue(nextValue);
  }, []);

  const handleAddContactInternal = useCallback(
    (contactId: string): void => {
      onAddContact(contactId);
    },
    [onAddContact],
  );

  const handleRemoveSelectedParticipant = useCallback(
    (participantId: string): void => {
      if (participantId.startsWith('contact:')) {
        onRemoveContact(participantId.replace('contact:', ''));
        return;
      }

      if (participantId.startsWith('manual:')) {
        const indexValue = Number(participantId.replace('manual:', ''));
        if (Number.isNaN(indexValue)) return;
        onRemoveNewExternalUser(indexValue);
      }
    },
    [onRemoveContact, onRemoveNewExternalUser],
  );

  const handleAddManualRow = useCallback((): void => {
    setManualRowValues((previousRows) => [
      ...previousRows,
      { id: createRowIdentifier(), values: EMPTY_MANUAL_VALUES },
    ]);
  }, []);

  const handleRemoveManualRow = useCallback((rowId: string): void => {
    setManualRowValues((previousRows) => {
      if (previousRows.length <= 1) return previousRows;
      return previousRows.filter((manualRow) => manualRow.id !== rowId);
    });
  }, []);

  const handleManualRowValuesChange = useCallback(
    (rowId: string, nextValues: ExternalInvitationManualValues): void => {
      setManualRowValues((previousRows) =>
        previousRows.map((manualRow) =>
          manualRow.id === rowId ? { ...manualRow, values: nextValues } : manualRow,
        ),
      );
    },
    [],
  );

  const handleAddRowToSix = useCallback(
    (rowId: string): void => {
      const targetRow = manualRowValues.find((manualRow) => manualRow.id === rowId);
      if (targetRow === undefined) return;

      if (resolveIsAddDisabled(targetRow.values)) return;

      onAddNewExternalUser({
        name: targetRow.values.name.trim(),
        email: targetRow.values.email.trim(),
        phoneNumber: targetRow.values.phoneNumber.trim(),
        department: targetRow.values.organization.trim(),
      });

      setManualRowValues((previousRows) =>
        previousRows.map((manualRow) =>
          manualRow.id === rowId ? { ...manualRow, values: EMPTY_MANUAL_VALUES } : manualRow,
        ),
      );

      setActiveTabValue('contacts');
    },
    [manualRowValues, onAddNewExternalUser],
  );

  return {
    state: {
      activeTabValue,
      contactsSearchValue,
      contactRows,
      selectedParticipants,
      manualRows,
    },
    handlers: {
      handleTabChange,
      handleContactsSearchValueChange,
      handleAddContact: handleAddContactInternal,
      handleRemoveSelectedParticipant,
      handleManualRowValuesChange,
      handleAddManualRow,
      handleRemoveManualRow,
      handleAddRowToSix,
    },
  };
}
